import React, { useCallback, useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import moment from "moment";
import AOS from "aos";
import "aos/dist/aos.css";
import { getFileCategoryType, getFileSize } from "../../utils/common";
import { toastError, toastSuccess } from "../../utils/toast";
import { usePageTitle } from "../PageTitleContext";
import { debounce } from "../../utils/useWindowDimensions";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { BtnLoading } from "../../animations/BtnLoading";
import { XR_ASSET_VERSION_LIST } from "../../apiClient/apiEndpoints";
import apiClient from "../../apiClient";
import Releases from "../Releases/releases";
import ProgressBar from 'react-bootstrap/ProgressBar';


const uploadTypes = [
  { key: "CSV", type: "csv", name: "Text (CSV)", fileTypes: ["csv"] },
  { key: "AUDIO", type: "audio", name: "Audio (ZIP)", fileTypes: ["zip"] },
  { key: "UI", type: "ui", name: "UI (ZIP)", fileTypes: ["zip"] },
];

const staticLanguageOptions = [
  { value: "english", label: "English" },
  { value: "hindi", label: "Hindi" },
  { value: "tamil", label: "Tamil" },
  { value: "telugu", label: "Telugu" },
];

const defaultUploadFile = {
  file: null,
  error: null,
};

const Languages = () => {
  const { uuid } = useParams();
  const [selectedLanguage, setSelectedLanguage] = useState(staticLanguageOptions[0]);
  const [reviewUploadFiles, setReviewUploadFiles] = useState([]);
  const [versionList, setVersionList] = useState([]);
  const [uploadComment, setUploadComment] = useState("");
  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    progress: 0
  });
  const [uploaderKey, setUploaderKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const { setPageTitle } = usePageTitle();
  const configData = useSelector((state) => state.configSlice);
  const userData = useSelector((state) => state.userSlice);

  useEffect(() => {
    setPageTitle("XR Modules");
  }, [setPageTitle]);

  useEffect(() => {
    AOS.init();
    AOS.refresh();
  }, []);

  const resetFileState = () => {
    setReviewUploadFiles([]);
    setUploadComment("");
    setUploaderKey((prev) => prev + 1);
  };

  const handleChangeLanguage = (value) => {
    const matchedLanguage = staticLanguageOptions.find((item) => String(item.value) === String(value)) || null;
    setSelectedLanguage(matchedLanguage);
  };

  const uploadFile = (fileOrFiles, type, key) => {

    if (!fileOrFiles) return;

    try {
      const files = fileOrFiles instanceof FileList ? Array.from(fileOrFiles) : [fileOrFiles];

      const newUploads = files.map((file) => {
        const isCsvFile = file.type === "text/csv" || file.name?.toLowerCase().endsWith(".csv");
        const isZipFile = getFileCategoryType(file) === "zip";
        let error = null;

        if (key === "CSV" && !isCsvFile) {
          error = "Please upload a CSV file.";
        } else if ((key === "AUDIO" || key === "UI") && !isZipFile) {
          error = "Please upload a ZIP file.";
        }

        return { ...defaultUploadFile, file, error, type, key };
      });

      setReviewUploadFiles((prev) => [
        ...prev.filter((item) => item.type !== type),
        ...newUploads,
      ]);

    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveFile = (item) => {
    setReviewUploadFiles((prev) => prev.filter((e) => e.key !== item.key));
  };

  const getModuleVersionList = useCallback(async () => {
    if (!selectedLanguage?.value || !uuid) return;
    setVersionList([]);
    try {
      let data = await apiClient.get(`${configData.config.UNO_URL}${XR_ASSET_VERSION_LIST}/${selectedLanguage.value}/${uuid}`);
      setVersionList(data);
    } catch (error) {
      console.log(error);
    }
  }, [configData.config.UNO_URL, selectedLanguage?.value, uuid]);

  useEffect(() => {
    getModuleVersionList();
  }, [getModuleVersionList]);

  const handleSaveLanguageAsset = async () => {

    try {

      if (!selectedLanguage?.value) {
        return toastError("Please select a language.");
      }

      const uploadedKeys = new Set(reviewUploadFiles.map((file) => file.key));
      const missingAssets = uploadTypes.filter((type) => !uploadedKeys.has(type.key));

      if (missingAssets.length > 0) {
        return toastError("Please upload all assets.");
      }

      if (!uploadComment?.trim()) {
        return toastError("Please enter a comment.");
      }

      const formData = new FormData();

      reviewUploadFiles.forEach(({ file, type }) => {
        if (file) {
          formData.append("files", file);
          formData.append("metadata", type);
        }
      });

      formData.append("comment", uploadComment.trim());

      setUploadProgress({
        isUploading: true,
        progress: 0
      });

      await axios.post(`${configData.config.UNO_URL}/api/resource/language/asset/upload/${selectedLanguage.value}/${uuid}`, formData, {
        headers: {
          Authorization: `Bearer ${userData?.token}`
        },
        onUploadProgress: ({ loaded, total }) => {
          const progress = Math.floor((loaded / total) * 100);
          console.log("progress ", progress);
          setUploadProgress({
            isUploading: true,
            progress: progress < 100 ? progress : 99,
          });
        }
      });


      toastSuccess("Language asset saved in local list.");
      resetFileState();
      getModuleVersionList();
      setUploadProgress(pre => ({
        ...pre,
        progress: Math.floor(100)
      }));

      setTimeout(() => {
        setUploadProgress({
          isUploading: false,
          progress: Math.floor(100)
        });
      }, 1500)

    } catch (error) {
      console.log(error);
      setUploadProgress({
        isUploading: false,
        progress: 0
      });
      toastError(error?.response?.data?.message || "Unable to save language asset.");
    } finally {
      setLoading(false);
    }
  };

  const debounceSaveLanguageAssets = debounce(handleSaveLanguageAsset, 300, setLoading);

  const uploadedKeys = new Set(reviewUploadFiles.map((file) => file.key));
  const missingAssets = uploadTypes.filter((type) => !uploadedKeys.has(type.key));

  return (
    <div className="forassets" data-aos="fade-left">
      <div className="container-fluid">
        <div className="row mt-2">
          <div className="col-md-9">
            <div className="platforupload">
              <div className="popupselect">
                <p>Languages</p>
                <div className="radio-inputs">
                  <FormControl fullWidth className="mb-3 d-flex align-items-start">
                    <RadioGroup
                      row
                      aria-labelledby="language-select-label"
                      name="languageSelect"
                      className="gap-3"
                      value={selectedLanguage?.value || ""}
                      onChange={(e) => handleChangeLanguage(e.target.value)}
                    >
                      {staticLanguageOptions.map((language, index) => (
                        <FormControlLabel
                          key={index}
                          className="quizlabel"
                          value={language.value}
                          control={<Radio />}
                          label={language.label}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12 mt-2">
            <div className="row">
              {/* <h6>Upload Files</h6> */}
              {uploadTypes.map((typeMeta) => (
                <div key={typeMeta.key} className="col-md-4">
                  <div className="popupselect fortype mb-3">
                    <p>Upload {typeMeta.name}</p>
                    <div className="DragDropMenu uploadfilesss mt-2">
                      <div className="fileuploader">
                        <FileUploader
                          key={`${uploaderKey}-${typeMeta.key}`}
                          handleChange={(file) => uploadFile(file, typeMeta.type, typeMeta.key)}
                          label="drag & drop or choose file to upload"
                          name={`file-${typeMeta.key}`}
                          multiple={typeMeta.key !== "text"}
                          types={typeMeta.fileTypes}

                        >
                          <span className="upload-box-info" style={{ fontSize: "12px" }}>
                            drag & drop {typeMeta.fileTypes.join("/")}
                          </span>
                        </FileUploader>
                      </div>
                    </div>


                    <div className="uploadstatus mt-3">

                      {reviewUploadFiles.filter(e => e.type === typeMeta.type).map((item, index) => (
                        <div key={index} className="upperdiv mb-2">
                          <div className="uploaddata">
                            <div className="xr">
                              <p className={item.error ? "text-danger" : ""}>
                                <strong>[{item.key}]</strong>{" "}
                                {item.file.name}
                              </p>
                              <p style={{ cursor: "pointer", lineHeight: "1" }} onClick={() => handleRemoveFile(item)}>
                                <i className="bi bi-x-circle-fill"></i>
                              </p>
                            </div>
                            <div className="xr mb-1">
                              <p className="filesize">{getFileSize(item.file.size)}</p>
                            </div>
                            {item.error && <p className="basicDetailError mb-0">{item.error}</p>}
                          </div>
                        </div>
                      ))}

                    </div>
                  </div>
                </div>
              ))}
            </div>



            <div className="mb-3 mt-2 VComment">
              <label htmlFor="languageComment" className="form-label">
                Comment
              </label>
              <textarea
                value={uploadComment}
                onChange={(e) => setUploadComment(e.target.value)}
                className="form-control"
                id="languageComment"
                rows="3"
              ></textarea>
            </div>

            {uploadProgress.isUploading && <ProgressBar animated striped variant="danger" label={`${uploadProgress.progress}%`} now={uploadProgress.progress} />}            

            <div className="deploybtn mt-3">
              {loading ?
                <button className="button" >
                  <BtnLoading />
                </button>
                :
                <button disabled={missingAssets.length > 0 ? true : false} onClick={debounceSaveLanguageAssets} className="btn btn-danger">
                  Upload
                </button>
              }
            </div>

          </div>

          <div className="col-md-12 mt-3 mb-3">
            {versionList.length > 0 ? (
              <div className="cardstyle">
                <div className="deploybtn d-flex justify-content-between">
                  <div>
                    <div className="popupselect fortype">
                      <p className="mb-0">
                        {versionList[0]?.createdAt ? moment(versionList[0]?.createdAt).fromNow() : null}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="filedetails">
                  <p>
                    Version :<span>{versionList[0]?.version}</span>
                  </p>
                  <p className="flex gap-2">
                    Asset :
                    {versionList?.[0]?.data?.assets?.map((item) => (
                      <span>
                        <a href={item.link} download>
                          {(item?.type).toUpperCase()}: {(item.link).split("/").at(-1)}
                        </a>
                      </span>
                    ))
                    }
                  </p>
                  <p>
                    Uploaded By :<span>{versionList[0]?.activities?.[0]?.users?.[0]?.name}</span>
                  </p>
                  <p>
                    Timestamp :
                    <span>
                      {versionList[0]?.createdAt
                        ? moment(versionList[0]?.createdAt).format("MMMM Do YYYY, h:mm a")
                        : null}
                    </span>
                  </p>
                  <p>
                    Comment :<span>{versionList[0]?.data?.comment}</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="cardstyle">
                <p className="mb-0 text-muted">No language release history yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="ReleaseHead">{versionList.length > 0 ? <h1>Releases</h1> : null}</div>
      {
        versionList?.map((list, index) => {
          return <Releases list={list} key={index + 1} isLanguageRelease={true} />;
        })
      }
    </div >
  );
};

export default Languages;
